// Hojas con cambios sin guardar (DS §6.17, UX §2.4, UX §2.8). Registro de módulo (sin React) que leen:
// - el bloqueador de navegación del shell (`SheetLeaveGuard`): Atrás, el riel, la barra inferior o cambiar de
//   local con una hoja sucia piden "¿Descartar los cambios?";
// - el aviso de sesión vencida: la hoja guarda su borrador en `sessionStorage` y, al volver a entrar, ofrece
//   "Recuperar lo que estabas cargando".
import type { FieldValues, UseFormReturn } from 'react-hook-form';

export interface SheetDraft {
  /** Valores serializables del formulario (p. ej. `form.getValues`). */
  get: () => unknown;
  /** Vuelve a cargar los valores guardados (p. ej. `form.reset(v, { keepDefaultValues: true })`). */
  restore: (value: unknown) => void;
}

interface OpenSheet {
  title: string;
  dirty: boolean;
  draft?: SheetDraft;
}

const open = new Map<symbol, OpenSheet>();
let allowUntil = 0;

/** Registra o actualiza una hoja abierta. Devuelve la función que la saca del registro. */
export function registerSheet(id: symbol, entry: OpenSheet): () => void {
  open.set(id, entry);
  return () => {
    open.delete(id);
  };
}

/** La hoja con cambios sin guardar más reciente, si hay. */
export function dirtySheet(): OpenSheet | undefined {
  let last: OpenSheet | undefined;
  for (const e of open.values()) if (e.dirty) last = e;
  return last;
}

/**
 * Deja pasar la navegación de este instante sin preguntar: cierres de hoja ya confirmados, cerrar después de
 * guardar, ir al ingreso por sesión vencida.
 */
export function allowSheetLeave(ms = 1000): void {
  allowUntil = Date.now() + ms;
}

export function sheetLeaveAllowed(): boolean {
  return Date.now() < allowUntil;
}

// ─────────────────────────────── Borradores (UX §2.8) ───────────────────────────────

const DRAFT_PREFIX = 'atte.draft:';
/** Un borrador viejo no se ofrece (la sesión de Atte dura 12 h sin uso). */
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;
let draftOwner: string | null = null;

/** Usuario dueño de los borradores (lo fija el shell): un borrador de otro usuario nunca se ofrece. */
export function setDraftOwner(userId: string | null): void {
  draftOwner = userId;
}

interface StoredDraft {
  owner: string | null;
  title: string;
  savedAt: number;
  value: unknown;
}

/** Clave del borrador: la URL de la hoja (ruta + parámetros ordenados). */
export function draftKey(pathname: string, search: string): string {
  const sp = new URLSearchParams(search);
  sp.sort();
  const qs = sp.toString();
  return `${DRAFT_PREFIX}${pathname}${qs ? `?${qs}` : ''}`;
}

function currentKey(): string {
  return draftKey(window.location.pathname, window.location.search);
}

/** Guarda el borrador de toda hoja abierta con cambios (al vencer la sesión). */
export function saveOpenDrafts(): void {
  const key = currentKey();
  for (const e of open.values()) {
    if (!e.dirty || !e.draft) continue;
    try {
      const stored: StoredDraft = { owner: draftOwner, title: e.title, savedAt: Date.now(), value: e.draft.get() };
      sessionStorage.setItem(key, JSON.stringify(stored));
    } catch {
      /* sin almacenamiento o valor no serializable: no hay borrador */
    }
  }
}

/** Borrador guardado para la URL actual (del mismo usuario y reciente). */
export function loadDraft(): { title: string; value: unknown } | null {
  try {
    const raw = sessionStorage.getItem(currentKey());
    if (!raw) return null;
    const d = JSON.parse(raw) as StoredDraft;
    if (Date.now() - d.savedAt > DRAFT_TTL_MS || (d.owner && draftOwner && d.owner !== draftOwner)) {
      sessionStorage.removeItem(currentKey());
      return null;
    }
    return { title: d.title, value: d.value };
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(currentKey());
  } catch {
    /* sin almacenamiento */
  }
}

/**
 * Borrador de un formulario de react-hook-form para `<Sheet draft>`: guarda los valores y al recuperar los
 * vuelve a cargar como cambios sin guardar.
 */
export function formDraft<T extends FieldValues>(form: Pick<UseFormReturn<T>, 'getValues' | 'reset'>): SheetDraft {
  return {
    get: () => form.getValues(),
    restore: (value) => form.reset(value as T, { keepDefaultValues: true }),
  };
}
